// Orb write actions. Every Supabase write is awaited (the builder is lazy —
// a bare call never fires). They mirror METIS / KYRS / THEMIS / CLIO's own writes.
import { useCallback, useRef, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { ensureAgendaRowForPickedTask } from '../../olmps/links/kyrsToMetis';
import { agendaTimeStr, dateKeyOf, hhmm } from './time';
import type { Acts, OrbData, OrbItem, Notes } from './types';

const nowIso = () => new Date().toISOString();
const genId = () => Math.random().toString(36).substring(2, 9);
const errText = (e: unknown) => (e instanceof Error ? e.message : typeof e === 'string' ? e : (e as { message?: string })?.message ?? 'Something went wrong');

/** A task-only item has no METIS row; today's can get one (same helper KYRS picks use). */
async function rowIdFor(userId: string, item: OrbItem): Promise<string | null> {
  if (item.rowId) return item.rowId;
  if (!item.taskId || item.date !== dateKeyOf(new Date())) return null;
  await ensureAgendaRowForPickedTask(userId, item.taskId, item.scheduledRaw ?? item.date);
  const { data } = await supabase.from('agenda_items').select('id').eq('user_id', userId).eq('date', item.date).eq('task_id', item.taskId).limit(1);
  return ((data ?? [])[0] as { id: string } | undefined)?.id ?? null;
}

/** Done / not done. Agenda rows: completed + cleared reason; a linked task follows (as METIS does). */
export async function writeDone(userId: string, item: OrbItem, value: boolean): Promise<string | null> {
  if (item.rowId) {
    const { error } = await supabase
      .from('agenda_items')
      .update({ completed: value, missed_reason: value ? '' : item.missed, updated_at: nowIso() })
      .eq('id', item.rowId)
      .eq('user_id', userId);
    if (error) return error.message;
  }
  if (item.taskId) {
    const { error } = await supabase.from('tasks').update({ done: value, updated_at: nowIso() }).eq('id', item.taskId).eq('user_id', userId);
    if (error) return error.message;
  } else if (!item.rowId) {
    return 'Nothing to mark here yet';
  }
  return null;
}

/** Not done, with a reason (METIS `missed_reason`). */
export async function writeMissed(userId: string, item: OrbItem, reason: string): Promise<string | null> {
  const id = await rowIdFor(userId, item);
  if (!id) return 'Open METIS to record a reason for this one';
  const { error } = await supabase.from('agenda_items').update({ completed: false, missed_reason: reason, updated_at: nowIso() }).eq('id', id).eq('user_id', userId);
  if (error) return error.message;
  if (item.taskId) {
    const t = await supabase.from('tasks').update({ done: false, updated_at: nowIso() }).eq('id', item.taskId).eq('user_id', userId);
    if (t.error) return t.error.message;
  }
  return null;
}

/** Reschedule to another day, keeping the time of day. Agenda rows move like METIS's auto-rescue (insert + delete); the linked task follows. */
export async function writeMove(userId: string, item: OrbItem, dayKey: string): Promise<string | null> {
  if (item.rowId) {
    const { data, error: readErr } = await supabase.from('agenda_items').select('*').eq('id', item.rowId).eq('user_id', userId).maybeSingle();
    if (readErr || !data) return readErr?.message ?? 'Could not find that row';
    const src = data as Record<string, any>;
    const row: Record<string, unknown> = {
      user_id: userId,
      date: dayKey,
      item_id: genId(),
      completed: false,
      text: src.text,
      time: src.time,
      duration: src.duration,
      sort_order: src.sort_order,
      nails: src.nails ?? [],
      design: src.design ?? null,
      task_id: src.task_id ?? null,
      sector: src.sector ?? null,
      design_id: src.design_id ?? null,
      aim_ref: src.aim_ref ?? null,
      updated_at: nowIso(),
    };
    if (src.note) row.note = src.note;
    const ins = await supabase.from('agenda_items').insert(row);
    if (ins.error) return ins.error.message;
    const del = await supabase.from('agenda_items').delete().eq('id', item.rowId).eq('user_id', userId);
    if (del.error) return del.error.message;
  }
  if (item.taskId) {
    const sched = item.hasTime && item.start ? `${dayKey} ${hhmm(item.start)}` : dayKey;
    const { error } = await supabase.from('tasks').update({ scheduled: sched, updated_at: nowIso() }).eq('id', item.taskId).eq('user_id', userId);
    if (error) return error.message;
  } else if (!item.rowId) {
    return 'Nothing to move here yet';
  }
  return null;
}

/** Re-time one item: agenda `time` (+ linked task `scheduled`) or the task's `scheduled`. */
async function writeTime(userId: string, item: OrbItem, at: Date): Promise<string | null> {
  if (item.rowId) {
    const { error } = await supabase.from('agenda_items').update({ time: agendaTimeStr(at), updated_at: nowIso() }).eq('id', item.rowId).eq('user_id', userId);
    if (error) return error.message;
  }
  if (item.taskId) {
    const { error } = await supabase.from('tasks').update({ scheduled: `${item.date} ${hhmm(at)}`, updated_at: nowIso() }).eq('id', item.taskId).eq('user_id', userId);
    if (error) return error.message;
  }
  return null;
}

/** Reorder = trade places. 'day' swaps the two items' days; 'slot' swaps their times of day (or sort order for untimed agenda rows). */
export async function writeSwap(userId: string, a: OrbItem, b: OrbItem, mode: 'day' | 'slot'): Promise<string | null> {
  if (mode === 'day') {
    if (a.date === b.date) return null;
    const e1 = await writeMove(userId, a, b.date);
    if (e1) return e1;
    return writeMove(userId, b, a.date);
  }
  if (a.start && b.start) {
    const ta = a.start;
    const tb = b.start;
    const e1 = await writeTime(userId, a, new Date(a.start.getFullYear(), a.start.getMonth(), a.start.getDate(), tb.getHours(), tb.getMinutes()));
    if (e1) return e1;
    return writeTime(userId, b, new Date(b.start.getFullYear(), b.start.getMonth(), b.start.getDate(), ta.getHours(), ta.getMinutes()));
  }
  if (a.rowId && b.rowId) {
    const r1 = await supabase.from('agenda_items').update({ sort_order: b.sort, updated_at: nowIso() }).eq('id', a.rowId).eq('user_id', userId);
    if (r1.error) return r1.error.message;
    const r2 = await supabase.from('agenda_items').update({ sort_order: a.sort, updated_at: nowIso() }).eq('id', b.rowId).eq('user_id', userId);
    return r2.error?.message ?? null;
  }
  return 'Give both a time first (KRNOS or METIS)';
}

/** A routine for a task: a THEMIS protocol (title + steps, "Set colours 10" = 10 min), found later by title. */
export async function writeRoutine(userId: string, title: string, lines: string[]): Promise<string | null> {
  const steps = lines
    .map((l) => l.trim())
    .filter(Boolean)
    .map((l) => {
      const m = /^(.*?)[\s,]+(\d{1,3})\s*(?:m|min|mins|minutes)?$/i.exec(l);
      return m && m[1].trim() ? { title: m[1].trim(), minutes: Math.max(1, Number(m[2])) } : { title: l, minutes: 5 };
    });
  if (!steps.length) return 'Write at least one step';
  const { count } = await supabase.from('protocols').select('id', { count: 'exact', head: true }).eq('user_id', userId).is('group_id', null);
  const { data: proto, error } = await supabase
    .from('protocols')
    .insert({ user_id: userId, title: title.trim() || 'Untitled step', group_id: null, order: count ?? 0, notes: '', created_at: nowIso() })
    .select('id')
    .single();
  if (error || !proto) return error?.message ?? 'Could not create the routine';
  const protocolId = (proto as { id: string }).id;
  const { error: itemErr } = await supabase.from('protocol_items').insert(
    steps.map((s, i) => ({ user_id: userId, protocol_id: protocolId, title: s.title, done: false, duration_minutes: s.minutes, order: i, created_at: nowIso() })),
  );
  return itemErr?.message ?? null;
}

/** Log a finished routine run (THEMIS's protocol_completions shape). */
export async function writeRoutineRun(userId: string, protocolId: string, protocolTitle: string, startedAt: number, stepsCompleted: number): Promise<void> {
  const secs = Math.max(1, Math.round((Date.now() - startedAt) / 1000));
  const { error } = await supabase.from('protocol_completions').insert({
    user_id: userId,
    protocol_id: protocolId,
    protocol_title: protocolTitle,
    started_at: new Date(startedAt).toISOString(),
    completed_at: nowIso(),
    status: 'success',
    steps_completed: stepsCompleted,
    steps_repeated: {},
    total_duration_seconds: secs,
    pause_count: 0,
  });
  if (error) console.warn('[orb] routine log failed', error.message);
}

/** A one-line lesson into today's CLIO entry (same journal_entries row CLIO's own save uses). */
export async function writeLesson(userId: string, clio: NonNullable<OrbData['clio']>, prefix: string, text: string): Promise<string | null> {
  const date = dateKeyOf(new Date());
  const line = `${prefix}${text.trim()}`;
  const { data, error } = await supabase
    .from('journal_entries')
    .select('id, answers')
    .eq('user_id', userId)
    .eq('date', date)
    .eq('entry_type_id', clio.typeId)
    .limit(1)
    .maybeSingle();
  if (error) return error.message;
  const now = nowIso();
  if (data) {
    const answers = { ...((data as { answers?: Record<string, string> }).answers ?? {}) };
    const cur = typeof answers[clio.promptId] === 'string' ? answers[clio.promptId] : '';
    answers[clio.promptId] = cur ? `${cur}\n${line}` : line;
    const r = await supabase.from('journal_entries').update({ answers, updated_at: now }).eq('id', (data as { id: string }).id);
    return r.error?.message ?? null;
  }
  const r = await supabase
    .from('journal_entries')
    .insert({ user_id: userId, date, entry_type_id: clio.typeId, answers: { [clio.promptId]: line }, exclude_from_epic: false, created_at: now, updated_at: now });
  return r.error?.message ?? null;
}

/** Bound actions for the rows. `after` runs once a write lands (refresh the orb + steps). */
export function useOrbActions(
  userId: string | undefined,
  data: OrbData,
  notes: Notes,
  after: () => void,
  nav: { drillDay: (k: string) => void; drillStep: (i: OrbItem) => void; go: Acts['go']; fail: (m: string) => void },
): Acts {
  const [busy, setBusy] = useState(false);
  const live = useRef({ data, notes, after, userId });
  live.current = { data, notes, after, userId };

  const run = useCallback(async (fn: (uid: string) => Promise<string | null>): Promise<boolean> => {
    const uid = live.current.userId;
    if (!uid) {
      nav.fail('Sign in to save');
      return false;
    }
    setBusy(true);
    try {
      const err = await fn(uid);
      if (err) {
        nav.fail(err);
        return false;
      }
      live.current.data.reload();
      live.current.after();
      return true;
    } catch (e) {
      nav.fail(errText(e));
      return false;
    } finally {
      setBusy(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    busy,
    done: (item, value = true) => run((u) => writeDone(u, item, value)),
    missed: (item, reason) => run((u) => writeMissed(u, item, reason)),
    move: (item, dayKey) => run((u) => writeMove(u, item, dayKey)),
    swap: (a, b, mode) => run((u) => writeSwap(u, a, b, mode)),
    makeRoutine: (title, lines) => run((u) => writeRoutine(u, title, lines)),
    lesson: async (kind, periodKey, text) => {
      const clio = live.current.data.clio;
      const t = text.trim();
      if (!t) return false;
      if (!clio) {
        nav.fail('Add a CLIO entry type with a text prompt first');
        return false;
      }
      const ok = await run((u) => writeLesson(u, clio, kind === 'week' ? 'Week · ' : 'Today · ', t));
      if (ok) live.current.notes.set(`L:${kind}:${periodKey}`, { text: t });
      return ok;
    },
    drillDay: nav.drillDay,
    drillStep: nav.drillStep,
    go: nav.go,
    fail: nav.fail,
  };
}

